import type { NextRoute } from '@savvy/deprecated-shared/generated/next-routes';
import type { TrackedClickableElementName } from '@savvy/deprecated-shared/tracking/tracked-clickable-element-name';
import defaultGuard from '@savvy/utils/default-guard';

export type InteractionType = 'button_click';
export type TrackedHtmlElement = 'button' | 'link';
export type TrackedButtonAddedProps = {
  elementName: TrackedClickableElementName;
  target?: '_blank';
  rel?: 'noreferrer';
};

/** @sorted */
export type PageName =
  | 'activities'
  | 'account-setup'
  | 'account-sleeve'
  | 'accounts'
  | 'advisor-book-onboarding'
  | 'advisor-directory'
  | 'professional-contacts'
  | 'alternative-funds'
  | 'animated-splash'
  | 'automated-proposal'
  | 'calendar'
  | 'canvas-document'
  | 'centers-of-influence'
  | 'checkin'
  | 'custodian-transfer'
  | 'custodian-transfer-expired'
  | 'custodian-transfer-success'
  | 'client-details'
  | 'client-home'
  | 'client-household-goals'
  | 'client-reports'
  | 'clients'
  | 'co-pilot'
  | 'confirm-email'
  | 'create-account'
  | 'create-password'
  | 'discovery'
  | 'documentation'
  | 'files'
  | 'financial-plan'
  | 'goals'
  | 'heldaway-account'
  | 'holdings'
  | 'household'
  | 'household-contact'
  | 'household-tax'
  | 'risk-questionnaire'
  | 'household-service-calendar'
  | 'household-opportunities'
  | 'internal-bulk-upload'
  | 'internal-account-opening'
  | 'internal-account-transfer'
  | 'internal-advisor-led-onboarding'
  | 'internal-bulk-household-upload'
  | 'internal-bulk-trades-upload'
  | 'internal-bulk-import-detail'
  | 'internal-bulk-imports'
  | 'internal-nfs-audit-logs'
  | 'internal-nfs-audit-search'
  | 'internal-bulk-onboarding-upload'
  | 'internal-advisor-book-upload'
  | 'internal-linked-account-upload'
  | 'internal-contribution-notifications-upload'
  | 'bulk-upload-docusign-envelope-csv-upload'
  | 'extract-household-documents'
  | 'internal-create-advisor'
  | 'internal-investments-general-config'
  | 'internal-marketing'
  | 'internal-setup-account-confirmation'
  | 'invalid-invite-token-dialog'
  | 'internal-login-as-advisor'
  | 'internal-export-client-info'
  | 'internal-upload-from-orion-to-box'
  | 'investment-proposals'
  | 'linked-account-upload'
  | 'login'
  | 'login-as-client'
  | 'logout-as'
  | 'managed-account'
  | 'marketing'
  | 'marketing-asset-details'
  | 'marketing-discovery'
  | 'my-account'
  | 'my-advisor'
  | 'net-worth'
  | 'offering'
  | 'offerings'
  | 'offering-type'
  | 'onboarding'
  | 'onboarding-dashboard'
  | 'opportunities'
  | 'organization-branding'
  | 'paperwork'
  | 'performance-reports'
  | 'planning'
  | 'portfolio'
  | 'portfolio-strategy'
  | 'portfolio-strategies'
  | 'published-report'
  | 'refresh-invite-token-dialog'
  | 'reset-password'
  | 'reporting'
  | 'retirement-planning'
  | 'rmds'
  | 'tags'
  | 'savvy-ai'
  | 'savvy-portfolio'
  | 'savvy-tax'
  | 'schedule'
  | 'service-calendar'
  | 'settings'
  | 'share-feedback'
  | 'report-issue'
  | 'tasks'
  | 'take-risk-questionnaire'
  | 'tax'
  | 'templates'
  | 'welcome'
  | 'workbench'
  | 'unknown';

export type TrackableRoute = NextRoute | '/files';

export function mapRouteToPageName(route: TrackableRoute): PageName | null {
  switch (route) {
    case '/account-setup':
      return 'account-setup';
    case '/custodian-transfer':
    case '/custodian-transfer/[stepId]/[[...pageId]]':
      return 'custodian-transfer';
    case '/custodian-transfer/expired':
      return 'custodian-transfer-expired';
    case '/custodian-transfer/success':
      return 'custodian-transfer-success';
    case '/advisor/dashboard/accounts':
      return 'accounts';
    case '/advisor/dashboard/alternative-funds':
      return 'alternative-funds';
    case '/advisor/dashboard/client/[salesforceAccountId]':
      return 'household';
    case '/advisor/dashboard/client/[salesforceAccountId]/details':
      return 'client-details';
    case '/advisor/dashboard/client/[salesforceAccountId]/contact/[contactId]':
      return 'household-contact';
    case '/advisor/dashboard/client/[salesforceAccountId]/files':
      return 'files';
    case '/advisor/dashboard/client/[salesforceAccountId]/published-reports':
    case '/advisor/dashboard/client/[salesforceAccountId]/reports':
    case '/advisor/dashboard/client/[salesforceAccountId]/scheduled-reports':
    case '/advisor/dashboard/client/[salesforceAccountId]/shared-materials':
      return 'client-reports';
    case '/advisor/dashboard/client/[salesforceAccountId]/net-worth':
      return 'net-worth';
    case '/advisor/dashboard/client/[salesforceAccountId]/planning':
      return 'planning';
    case '/advisor/dashboard/client/[salesforceAccountId]/retirement-planning':
      return 'retirement-planning';
    case '/advisor/dashboard/client/[salesforceAccountId]/portfolio/[portfolioId]':
      return 'portfolio';
    case '/advisor/dashboard/client/[salesforceAccountId]/portfolio/account/[managedAccountId]':
      return 'managed-account';
    case '/advisor/dashboard/client/[salesforceAccountId]/portfolio/account/sleeve/[accountSleeveId]':
      return 'account-sleeve';
    case '/advisor/dashboard/client/[salesforceAccountId]/portfolio/external-account/[heldawayAccountId]':
      return 'heldaway-account';
    case '/advisor/dashboard/client/[salesforceAccountId]/portfolio/strategy':
      return 'portfolio-strategy';
    case '/advisor/dashboard/client/[salesforceAccountId]/portfolio/strategy/[portfolioStrategyId]':
      return 'portfolio-strategy';
    case '/advisor/dashboard/client/[salesforceAccountId]/risk-questionnaire':
      return 'risk-questionnaire';
    case '/advisor/dashboard/client/[salesforceAccountId]/service-calendar':
    case '/advisor/dashboard/client/[salesforceAccountId]/service-calendar-v3':
      return 'household-service-calendar';
    case '/advisor/dashboard/client/[salesforceAccountId]/opportunities':
      return 'household-opportunities';
    case '/advisor/dashboard/client/[salesforceAccountId]/paperwork':
      return 'paperwork';
    case '/advisor/dashboard/client/[salesforceAccountId]/tax':
      return 'household-tax';
    case '/advisor/dashboard/clients':
      return 'clients';
    case '/advisor/dashboard/co-pilot':
    case '/advisor/dashboard/co-pilot/v2':
      return 'co-pilot';
    case '/advisor/dashboard/cois':
      return 'centers-of-influence';
    case '/advisor/dashboard/linked-bank-accounts':
      return 'linked-account-upload';
    case '/advisor/dashboard/marketing':
      return 'marketing';
    case '/advisor/dashboard/marketing/asset/[boxFileId]':
      return 'marketing-asset-details';
    case '/advisor/dashboard/marketing/discovery':
      return 'marketing-discovery';
    case '/advisor/dashboard/offerings':
      return 'offerings';
    case '/advisor/dashboard/offerings/[offeringId]':
      return 'offering';
    case '/advisor/dashboard/offerings/type/[offeringType]':
      return 'offering-type';
    case '/advisor/dashboard/opportunities':
      return 'opportunities';
    case '/advisor/dashboard/organization-branding':
      return 'organization-branding';
    case '/advisor/dashboard/onboarding':
    case '/advisor/dashboard/onboarding/[onboardingProcessId]':
    case '/advisor/dashboard/onboarding/flow/[householdPublicId]/[[...step]]':
      return 'advisor-book-onboarding';
    case '/advisor/dashboard/rmds':
      return 'rmds';
    case '/advisor/dashboard/service-items':
    case '/advisor/dashboard/service-calendar':
    case '/advisor/dashboard/service-calendar/v3':
      return 'service-calendar';
    case '/advisor/dashboard/settings':
      return 'settings';
    case '/advisor/dashboard/client/[salesforceAccountId]/new-task':
    case '/advisor/dashboard/client/[salesforceAccountId]/new-task/account-confirmation':
    case '/advisor/dashboard/client/[salesforceAccountId]/new-task/account-maintenance':
    case '/advisor/dashboard/client/[salesforceAccountId]/new-task/alternative-investments-purchase':
    case '/advisor/dashboard/client/[salesforceAccountId]/new-task/client-info-change':
    case '/advisor/dashboard/client/[salesforceAccountId]/new-task/client-billing':
    case '/advisor/dashboard/client/[salesforceAccountId]/new-task/client-onboarding':
    case '/advisor/dashboard/client/[salesforceAccountId]/new-task/corporate-retirement-plan':
    case '/advisor/dashboard/client/[salesforceAccountId]/new-task/file-upload':
    case '/advisor/dashboard/client/[salesforceAccountId]/new-task/intake':
    case '/advisor/dashboard/client/[salesforceAccountId]/new-task/money-movement':
    case '/advisor/dashboard/client/[salesforceAccountId]/new-task/onboarding':
    case '/advisor/dashboard/client/[salesforceAccountId]/new-task/ops-other':
    case '/advisor/dashboard/client/[salesforceAccountId]/new-task/other-client':
    case '/advisor/dashboard/client/[salesforceAccountId]/new-task/other-investment-task':
    case '/advisor/dashboard/client/[salesforceAccountId]/new-task/prereview-checkin':
    case '/advisor/dashboard/client/[salesforceAccountId]/new-task/risk-questionnaire':
    case '/advisor/dashboard/client/[salesforceAccountId]/new-task/swim':
    case '/advisor/dashboard/client/[salesforceAccountId]/new-task/task':
    case '/advisor/dashboard/client/[salesforceAccountId]/new-task/trading':
    case '/advisor/dashboard/client/[salesforceAccountId]/new-task/open-new-account':
    case '/advisor/dashboard/client/[salesforceAccountId]/new-task/transfer-of-assets':
    case '/advisor/dashboard/client/[salesforceAccountId]/new-task/transition-analysis':
    case '/advisor/dashboard/client/[salesforceAccountId]/new-task/freeform':
    case '/advisor/dashboard/client/[salesforceAccountId]/upgrade-custodian/[stepName]/[[...pageId]]':
    case '/advisor/dashboard/client/[salesforceAccountId]/upgrade-custodian/confirmation':
    case '/advisor/dashboard/tasks/new':
    case '/advisor/dashboard/tasks/new/account-confirmation':
    case '/advisor/dashboard/tasks/new/account-maintenance':
    case '/advisor/dashboard/tasks/new/alternative-investments-purchase':
    case '/advisor/dashboard/tasks/new/client-info-change':
    case '/advisor/dashboard/tasks/new/client-billing':
    case '/advisor/dashboard/tasks/new/client-onboarding':
    case '/advisor/dashboard/tasks/new/corporate-retirement-plan':
    case '/advisor/dashboard/tasks/new/file-upload':
    case '/advisor/dashboard/tasks/new/intake':
    case '/advisor/dashboard/tasks/new/money-movement':
    case '/advisor/dashboard/tasks/new/onboarding':
    case '/advisor/dashboard/tasks/new/ops-other':
    case '/advisor/dashboard/tasks/new/other-client':
    case '/advisor/dashboard/tasks/new/other-investment-task':
    case '/advisor/dashboard/tasks/new/prereview-checkin':
    case '/advisor/dashboard/tasks/new/risk-questionnaire':
    case '/advisor/dashboard/tasks/new/swim':
    case '/advisor/dashboard/tasks/new/task':
    case '/advisor/dashboard/tasks/new/trading':
    case '/advisor/dashboard/tasks/new/open-new-account':
    case '/advisor/dashboard/tasks/new/transfer-of-assets':
    case '/advisor/dashboard/tasks/new/transition-analysis':
    case '/advisor/dashboard/tasks/new/freeform':
    case '/advisor/dashboard/tasks':
    case '/advisor/dashboard/tasks/docusign-tracker':
      return 'tasks';
    case '/advisor/login-as/[clientUserId]':
      return 'login-as-client';
    case '/advisor/workbench':
    case '/advisor/workbench/[conversationId]':
    case '/advisor/canvas':
    case '/advisor/canvas/[conversationId]':
    case '/advisor/canvas/household/[salesforceAccountId]':
      return 'workbench';
    case '/animated-splash':
      return 'animated-splash';
    case '/checkin':
      return 'checkin';
    case '/confirm-email':
      return 'confirm-email';
    case '/create-account':
      return 'create-account';
    case '/create-password':
      return 'create-password';
    case '/dashboard':
      return 'client-home';
    case '/dashboard/account':
      return 'my-account';
    case '/dashboard/advisor':
      return 'my-advisor';
    case '/dashboard/reports':
      return 'client-reports';
    case '/dashboard/reports/[id]':
      return 'published-report';
    case '/files':
    case '/files/[[...boxPath]]':
      return 'files';
    case '/financial-plan':
      return 'financial-plan';
    case '/goals':
      return 'goals';
    case '/risk-questionnaires/take':
      return 'take-risk-questionnaire';
    case '/internal/admin/marketing':
      return 'internal-marketing';
    case '/internal/admin/investments/general-config':
      return 'internal-investments-general-config';
    case '/internal/admin/ops/create-advisor':
      return 'internal-create-advisor';
    case '/internal/admin/login-as-advisor':
      return 'internal-login-as-advisor';
    case '/internal/admin/ops/export-client-info':
      return 'internal-export-client-info';
    case '/internal/admin/ops/upload-from-orion-to-box':
      return 'internal-upload-from-orion-to-box';
    case '/internal/admin/ops/extract-household-documents':
      return 'extract-household-documents';
    case '/invalid-invite-token-dialog':
      return 'invalid-invite-token-dialog';
    case '/login':
      return 'login';
    case '/logout-as':
      return 'logout-as';
    case '/onboarding':
      return 'onboarding';
    case '/portfolio':
      return 'portfolio';
    case '/portfolio/accounts/[managedAccountId]':
      return 'managed-account';
    case '/portfolio/external-accounts/[heldawayAccountId]':
      return 'heldaway-account';
    case '/portfolio/holdings':
      return 'holdings';
    case '/refresh-invite-token-dialog':
      return 'refresh-invite-token-dialog';
    case '/reset-password':
      return 'reset-password';
    case '/savvy-portfolio/[portfolioId]':
      return 'savvy-portfolio';
    case '/schedule/[advisorId]':
      return 'schedule';
    case '/advisor/dashboard/share_feedback':
      return 'share-feedback';
    case '/advisor/dashboard/report_issue':
      return 'report-issue';
    case '/advisor/dashboard/documentation/[[...path]]':
      return 'documentation';
    case '/tasks':
      return 'tasks';
    case '/welcome':
      return 'welcome';
    case '/advisor/dashboard/canvas-document/view':
      return 'canvas-document';
    case '/advisor/dashboard/reporting':
    case '/advisor/dashboard/reporting/view':
      return 'reporting';
    case '/advisor/dashboard/reporting/investment-proposals':
      return 'investment-proposals';
    case '/advisor/dashboard/reporting/performance-reports':
      return 'performance-reports';
    case '/advisor/dashboard/directory':
      return 'advisor-directory';
    case '/advisor/dashboard/professional-contacts':
    case '/advisor/dashboard/professional-contacts/[centerOfInfluenceRepId]':
    case '/advisor/dashboard/professional-contacts/businesses/[companyId]':
      return 'professional-contacts';
    case '/advisor/dashboard/savvy-tax':
      return 'savvy-tax';
    case '/advisor/dashboard/tags':
      return 'tags';
    case '/advisor/dashboard/templates':
    case '/advisor/dashboard/templates/new':
    case '/advisor/dashboard/templates/workflows':
    case '/advisor/dashboard/templates/workflows/new':
    case '/advisor/dashboard/templates/services':
    case '/advisor/dashboard/templates/services/new':
    case '/advisor/dashboard/templates/services/[id]':
      return 'templates';
    case '/dashboard/tax':
      return 'tax';
    case '/dashboard/tax/tax-consent':
      return 'tax';
    case '/dashboard/tax/tax-filing':
      return 'tax';
    case '/dashboard/tax/tax-addendum':
      return 'tax';
    case '/internal/admin/ops/advisor-led-onboarding':
      return 'internal-advisor-led-onboarding';
    case '/internal/admin/ops/bulk-household-upload':
      return 'internal-bulk-household-upload';
    case '/internal/admin/ops/bulk-trades-upload':
      return 'internal-bulk-trades-upload';
    case '/internal/admin/ops/bulk-onboarding-upload':
      return 'internal-bulk-onboarding-upload';
    case '/internal/admin/ops/advisor-book-upload':
      return 'internal-advisor-book-upload';
    case '/internal/admin/ops/linked-account-upload':
      return 'internal-linked-account-upload';
    case '/internal/admin/ops/contribution-notifications-upload':
      return 'internal-contribution-notifications-upload';
    case '/internal/admin/ops/nfs-audit-logs':
      return 'internal-nfs-audit-logs';
    case '/internal/admin/ops/nfs-audit-search':
      return 'internal-nfs-audit-search';
    case '/internal/admin/ops/bulk-imports':
      return 'internal-bulk-imports';
    case '/internal/admin/ops/bulk-imports/[id]':
      return 'internal-bulk-import-detail';
    case '/advisor':
      return null; // redirects to clients page
    case '/advisor/dashboard':
    case '/401':
    case '/404':
    case '/500':
    case '/advisor/dashboard/auth':
    case '/advisor/dashboard/callback':
    case '/advisor/dashboard/trigger-error':
    case '/advisor/error-testing':
    case '/index': // TODO: Fix
    case '/internal/admin/ops/[advisorId]':
    case '/plaid-redirect':
    case '/moneykit-redirect':
    case '/redirect-to-mobile-client-app/reset-password':
    case '/internal/admin/ops/oauth-callback':
    case '/internal/admin/ops/schwab-oauth-callback':
    case '/internal/admin/ops/export-service-calendar':
    case '/internal/admin/ops/generate-quarterly-reports':
    case '/internal/admin/organizations':
    case '/advisor/dashboard/client/user/[userId]':
    case '/oauth/authorize':
    case '/financial-planning':
    case '/internal/planning-tools-demo':
    case '/advisor/dashboard/retirement-plans':
    case '/advisor/dashboard/retirement-plans/[planId]':
    case '/advisor/dashboard/retirement-plans/[planId]/projects/[projectId]':
      return null;
    // Internal admin tool; page-view tracking intentionally omitted (no PageName).
    case '/internal/admin/grant-advisor-permissions':
      return null;
    // Demo-only internal tool; page-view tracking intentionally omitted (no PageName).
    case '/advisor/dashboard/demo-reseed':
      return null;
    default:
      return defaultGuard(route, 'unknown');
  }
}
